import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown, Eye, Loader2, MapPin, Search, Send, Settings2, Trash2, Users,
} from "lucide-react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import {
  createPropertyMarketUpdate, deleteMarketUpdateReport, getMarketUpdateContacts,
  getMarketUpdateReports,
} from "../api/prospectingReportsApi";
import { getAgents } from "../../../api/agent";
import CreateMarketUpdateModal from "../components/CreateMarketUpdateModal";
import SendMarketUpdateModal from "../components/SendMarketUpdateModal";
import NewAddressReportModal from "../components/NewAddressReportModal";
import { hasAdminAccess } from "../../../utils/roleAccess";
import { MU_RED, SKY, fmtInt } from "../marketUpdateTheme";
import { formatMelbourneShortDate } from "../../../utils/melbourneTime";

const PAGE_SIZE = 50;

const SOURCES = [
  { key: "all", label: "All" },
  { key: "pipeline", label: "Pipeline" },
  { key: "database", label: "Database" },
];

const SOURCE_PILL = {
  appraisal: "bg-[#eef6ff] text-[#1d4f91]",
  seller_lead: "bg-[#fff4e5] text-[#9a5b00]",
  database: "bg-[#f2f3f5] text-[#4b5563]",
};

const STATUS_STYLES = {
  draft: "bg-[#f2f3f5] text-[#4b5563]",
  sent: "bg-[#e9f7ee] text-[#1f7a46]",
};

const auDate = formatMelbourneShortDate;

/** The red split button: Create report, plus the less common ways to start one. */
const CreateReportMenu = ({ onNewAddress, onSuburbEmail, onTemplates }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const item = (label, hint, onClick) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => { setOpen(false); onClick(); }}
      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
    >
      <span className="block text-sm font-semibold text-gray-900">{label}</span>
      <span className="block text-xs text-gray-500">{hint}</span>
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 pl-5 pr-4 py-2.5 rounded-lg text-white text-sm font-semibold shadow-sm transition-colors hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#e4002b]"
        style={{ backgroundColor: MU_RED }}
      >
        Create report
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-72 rounded-xl border border-gray-100 bg-white shadow-[0_12px_32px_-8px_rgba(17,24,39,0.18)] py-1 z-30">
          {item("For a new address", "An owner who is not in your Pipeline or Database yet.", onNewAddress)}
          {item("Suburb email to a client list", "The template email with suburb figures.", onSuburbEmail)}
          <div className="my-1 border-t border-gray-100" />
          {item("Manage email templates", "Templates for the suburb email.", onTemplates)}
        </div>
      )}
    </div>
  );
};

const SkeletonRows = () => (
  <div className="divide-y divide-gray-100" aria-hidden="true">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 px-4 py-4">
        <div className="h-3.5 w-40 rounded bg-gray-100 animate-pulse" />
        <div className="h-3.5 flex-1 rounded bg-gray-100 animate-pulse" />
        <div className="h-8 w-28 rounded-lg bg-gray-100 animate-pulse" />
      </div>
    ))}
  </div>
);

/**
 * Market Update. Christian Gravias, Monday 13377764940: "when you click market
 * update you show a list of contacts that are either in an agents pipeline or
 * database. Show in list view and have this red button to begin."
 *
 * Contacts is the agent's own owners; Sent & drafts is every report built,
 * including the older suburb emails.
 */
const MarketUpdatePage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isOfficeWide = hasAdminAccess(user) || (user?.role || "").toLowerCase() === "pa";

  const [tab, setTab] = useState("contacts");

  // ---- contacts
  const [contacts, setContacts] = useState([]);
  const [counts, setCounts] = useState({ count: 0, pipeline_count: 0, database_count: 0 });
  const [contactsLoading, setContactsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [source, setSource] = useState("all");
  const [startingKey, setStartingKey] = useState("");

  // ---- reports
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportSearch, setReportSearch] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [agents, setAgents] = useState([]);

  const [showNewAddress, setShowNewAddress] = useState(false);
  const [showSuburbCreate, setShowSuburbCreate] = useState(false);
  const [sendFor, setSendFor] = useState(null);

  const loadContacts = useCallback(async (offset = 0) => {
    if (offset === 0) setContactsLoading(true); else setLoadingMore(true);
    try {
      const params = { source, limit: PAGE_SIZE, offset };
      if (contactSearch.trim()) params.q = contactSearch.trim();
      const { data } = await getMarketUpdateContacts(params);
      setContacts((prev) => (offset === 0 ? data.results : [...prev, ...data.results]));
      setCounts({ count: data.count, pipeline_count: data.pipeline_count, database_count: data.database_count });
    } catch (error) {
      toast.error("Could not load your contacts.");
      if (offset === 0) setContacts([]);
    } finally {
      setContactsLoading(false);
      setLoadingMore(false);
    }
  }, [source, contactSearch]);

  useEffect(() => {
    const timer = setTimeout(() => loadContacts(0), 300);
    return () => clearTimeout(timer);
  }, [loadContacts]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const params = {};
      if (reportSearch.trim()) params.q = reportSearch.trim();
      if (agentFilter) params.agent = agentFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await getMarketUpdateReports(params);
      setReports(data?.results || []);
    } catch (error) {
      toast.error("Could not load your market updates.");
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, [reportSearch, agentFilter, statusFilter]);

  useEffect(() => {
    if (tab !== "reports") return undefined;
    const timer = setTimeout(loadReports, 300);
    return () => clearTimeout(timer);
  }, [tab, loadReports]);

  useEffect(() => {
    if (!isOfficeWide) return;
    getAgents()
      .then(({ data }) => setAgents(Array.isArray(data) ? data : data?.results || []))
      .catch(() => setAgents([]));
  }, [isOfficeWide]);

  const startReport = async (contact) => {
    setStartingKey(contact.key);
    try {
      const { data } = await createPropertyMarketUpdate({
        source_type: contact.source_type, source_id: contact.source_id,
      });
      navigate(`/prospecting/market-update/${data.id}`);
    } catch (error) {
      // A field error ({"suburb": [...]}) has no `detail`; a 500 is an HTML string.
      const body = error?.response?.data;
      const detail = body && typeof body === "object" ? body : {};
      const fieldError = Object.values(detail)[0];
      toast.error(detail.detail || (Array.isArray(fieldError) ? fieldError[0] : "") || "Could not start the report.");
      setStartingKey("");
    }
  };

  const handleDelete = async (report) => {
    const label = report.full_address || report.suburb;
    if (!window.confirm(`Delete the market update for ${label}?`)) return;
    try {
      await deleteMarketUpdateReport(report.id);
      setReports((prev) => prev.filter((r) => r.id !== report.id));
      toast.success("Deleted.");
    } catch (error) {
      toast.error("Could not delete it.");
    }
  };

  const tabButton = (key, label, count) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      onClick={() => setTab(key)}
      className={`relative px-1 pb-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:text-gray-900 ${
        tab === key ? "text-gray-900" : "text-gray-500 hover:text-gray-800"
      }`}
    >
      {label}
      {count != null && <span className="ml-1.5 text-xs font-medium text-gray-400 tabular-nums">{fmtInt(count)}</span>}
      {tab === key && <span className="absolute left-0 right-0 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: SKY }} />}
    </button>
  );

  const sourceCount = (key) => (
    key === "pipeline" ? counts.pipeline_count : key === "database" ? counts.database_count
      : counts.pipeline_count + counts.database_count
  );

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Market Update</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Choose an owner from your Pipeline or Database, pick the sales around them, and send it by SMS and email.
          </p>
        </div>
        <CreateReportMenu
          onNewAddress={() => setShowNewAddress(true)}
          onSuburbEmail={() => setShowSuburbCreate(true)}
          onTemplates={() => navigate("/prospecting/market-update/templates")}
        />
      </div>

      <div className="flex gap-6 border-b border-gray-200 mb-5" role="tablist">
        {tabButton("contacts", "Contacts", counts.pipeline_count + counts.database_count)}
        {tabButton("reports", "Sent & drafts")}
      </div>

      {tab === "contacts" && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <input
                type="search"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Search owner, address, phone or email"
                aria-label="Search contacts"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#55c5d0] focus:ring-2 focus:ring-[#55c5d0]/15"
              />
            </div>
            <div className="inline-flex p-1 rounded-lg bg-gray-100" role="radiogroup" aria-label="Where the contacts come from">
              {SOURCES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="radio"
                  aria-checked={source === s.key}
                  onClick={() => setSource(s.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#55c5d0]/30 ${
                    source === s.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {s.label}
                  {!contactsLoading && source === s.key && (
                    <span className="ml-1.5 text-xs text-gray-400 tabular-nums">{fmtInt(sourceCount(s.key))}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="hidden md:grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_120px_120px_150px] gap-4 px-4 py-2.5 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500">
              <span>Owner</span>
              <span>Property</span>
              <span>From</span>
              <span>Last update</span>
              <span className="sr-only">Action</span>
            </div>

            {contactsLoading ? (
              <SkeletonRows />
            ) : contacts.length === 0 ? (
              <div className="px-6 py-14 text-center">
                <MapPin className="w-9 h-9 mx-auto text-gray-300 mb-3" aria-hidden="true" />
                <p className="text-sm font-semibold text-gray-800">
                  {contactSearch.trim() ? "No owners match that search." : "No owners to send to yet."}
                </p>
                <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                  Owners appear here once they are in your Database, Seller Leads or appraisals.
                  You can also start a report for any address from Create report.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {contacts.map((c) => (
                  <li
                    key={c.key}
                    className="grid grid-cols-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_120px_120px_150px] gap-x-4 gap-y-1.5 px-4 py-3.5 md:items-center hover:bg-gray-50/70"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {[c.mobile, c.email].filter(Boolean).join(" · ") || "No mobile or email"}
                      </p>
                      {c.unsubscribed && (
                        <p className="text-xs font-semibold text-[#b42318] mt-0.5">Unsubscribed</p>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 truncate min-w-0" title={c.address}>
                      {c.address || <span className="text-gray-500">No address recorded</span>}
                    </p>
                    <div>
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${SOURCE_PILL[c.source_type] || SOURCE_PILL.database}`}>
                        {c.source_label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 tabular-nums">
                      <span className="md:hidden text-gray-500">Last update: </span>
                      {c.last_update_at ? auDate(c.last_update_at) : "Never"}
                    </p>
                    <div className="md:text-right pt-1 md:pt-0">
                      <button
                        type="button"
                        onClick={() => startReport(c)}
                        disabled={!!startingKey || !c.address}
                        title={c.address ? undefined : "This owner has no property address yet."}
                        className="inline-flex items-center justify-center gap-1.5 w-full md:w-auto min-h-[40px] px-4 rounded-lg text-white text-sm font-semibold transition hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#e4002b]"
                        style={{ backgroundColor: MU_RED }}
                      >
                        {startingKey === c.key && <Loader2 className="w-4 h-4 animate-spin" />}
                        Create report
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {!contactsLoading && contacts.length < counts.count && (
            <div className="flex justify-center mt-4">
              <button
                type="button"
                onClick={() => loadContacts(contacts.length)}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Show more ({fmtInt(counts.count - contacts.length)} left)
              </button>
            </div>
          )}
        </>
      )}

      {tab === "reports" && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
              <input
                type="search"
                value={reportSearch}
                onChange={(e) => setReportSearch(e.target.value)}
                placeholder="Search address or suburb"
                aria-label="Search reports"
                className="pl-9 pr-3 py-2 w-64 rounded-lg border border-gray-200 text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#55c5d0] focus:ring-2 focus:ring-[#55c5d0]/15"
              />
            </div>
            {isOfficeWide && (
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                aria-label="Lead agent"
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
              >
                <option value="">All agents</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.name || agent.email}</option>
                ))}
              </select>
            )}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Status"
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              <option value="">Drafts and sent</option>
              <option value="draft">Drafts</option>
              <option value="sent">Sent</option>
            </select>
          </div>

          {reportsLoading ? (
            <div className="bg-white border border-gray-200 rounded-xl"><SkeletonRows /></div>
          ) : reports.length === 0 ? (
            <div className="border border-dashed border-gray-200 rounded-xl py-14 text-center">
              <p className="text-sm font-semibold text-gray-800">No market updates yet.</p>
              <p className="text-sm text-gray-500 mt-1">Start one from the Contacts tab.</p>
            </div>
          ) : (
            <ul className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
              {reports.map((report) => {
                const isProperty = report.kind === "property";
                return (
                  <li key={report.id} className="flex flex-wrap md:flex-nowrap items-center gap-4 px-4 py-3.5">
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-sm font-semibold text-gray-900">
                        {isProperty ? report.full_address : report.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {isProperty
                          ? (report.vendors || []).map((v) => [v.first_name, v.last_name].filter(Boolean).join(" ")).filter(Boolean).join(", ") || "No vendor yet"
                          : `Suburb email · ${[report.suburb, report.state].filter(Boolean).join(" ")}`}
                      </p>
                    </div>
                    {isOfficeWide && (
                      <p className="hidden lg:block w-36 text-xs text-gray-600 truncate">{report.agent?.name}</p>
                    )}
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[report.status] || ""}`}>
                      {report.status === "sent" ? "Sent" : "Draft"}
                    </span>
                    <p className="hidden md:flex items-center gap-1 w-20 text-xs text-gray-600 tabular-nums">
                      <Users className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
                      {report.recipient_count || 0}
                    </p>
                    <p className="hidden md:block w-28 text-xs text-gray-600 tabular-nums">
                      {auDate(report.sent_at || report.created_at)}
                    </p>
                    <div className="flex items-center gap-1 ml-auto">
                      {isProperty ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/prospecting/market-update/${report.id}${report.status === "sent" ? "?step=preview" : ""}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                        >
                          {report.status === "sent" ? <><Eye className="w-3.5 h-3.5" /> Open</> : "Continue"}
                        </button>
                      ) : (
                        <>
                          <a
                            href={`/market-update/${report.share_token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                          >
                            <Eye className="w-3.5 h-3.5" /> Preview
                          </a>
                          <button
                            type="button"
                            onClick={() => setSendFor(report)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:text-gray-900"
                          >
                            <Send className="w-3.5 h-3.5" /> Send
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(report)}
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-700"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            onClick={() => navigate("/prospecting/market-update/templates")}
            className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-gray-600 hover:text-gray-900"
          >
            <Settings2 className="w-4 h-4" /> Manage suburb email templates
          </button>
        </>
      )}

      {showNewAddress && <NewAddressReportModal onClose={() => setShowNewAddress(false)} />}

      {showSuburbCreate && (
        <CreateMarketUpdateModal
          onClose={() => setShowSuburbCreate(false)}
          onCreated={(report) => {
            setShowSuburbCreate(false);
            setTab("reports");
            setReports((prev) => [report, ...prev]);
            setSendFor(report);
          }}
        />
      )}

      {sendFor && (
        <SendMarketUpdateModal
          report={sendFor}
          onClose={() => setSendFor(null)}
          onSent={(updated) => {
            setReports((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            setSendFor(null);
          }}
        />
      )}
    </div>
  );
};

export default MarketUpdatePage;
